// AI Recording Studio Pro — Voice AI
// Real-time transcription, subtitle generation, and voice analysis
import { useState, useRef, useCallback, useEffect } from "react";
import { RecordingStudioLayout } from "@/components/layout/RecordingStudioLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic, MicOff, Play, Square, Pause, Download, Copy,
  FileText, Subtitles, Languages, Volume2, VolumeX,
  Clock, Hash, Type, RefreshCw, Trash2, Settings2,
  Wand2, CheckCircle2, AlertCircle, Loader2,
  BookOpen, ListChecks, Sparkles, Zap,
} from "lucide-react";
import { toast } from "sonner";

const LANGUAGES = [
  { value: "fr-FR", label: "Français" },
  { value: "en-US", label: "Anglais (US)" },
  { value: "en-GB", label: "Anglais (UK)" },
  { value: "ar-SA", label: "Arabe" },
  { value: "de-DE", label: "Allemand" },
  { value: "es-ES", label: "Espagnol" },
];

export default function RecordingVoiceAI() {
  // Recording
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimText, setInterimText] = useState("");
  const [language, setLanguage] = useState("fr-FR");
  const [duration, setDuration] = useState(0);
  const [wordCount, setWordCount] = useState(0);

  // Features
  const [autoChapters, setAutoChapters] = useState(true);
  const [autoCorrect, setAutoCorrect] = useState(false);
  const [showSubtitles, setShowSubtitles] = useState(true);

  // Results
  const [chapters, setChapters] = useState([]);
  const [subtitles, setSubtitles] = useState([]);

  // Refs
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Check Web Speech API support
  const isSupported = typeof window !== "undefined" && ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  // Start listening
  const startListening = useCallback(() => {
    if (!isSupported) {
      toast.error("Web Speech API non supportée dans ce navigateur");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let interim = "";
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const resultText = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += resultText + " ";
        } else {
          interim += resultText;
        }
      }

      if (final) {
        setTranscript((prev) => {
          const updated = prev + final;
          setWordCount(updated.split(/\s+/).filter(Boolean).length);

          // Auto-detect chapters (pauses > 3 seconds or key phrases)
          if (autoChapters && (final.toLowerCase().includes("chapitre") || final.toLowerCase().includes("partie") || final.toLowerCase().includes("section"))) {
            const timestamp = formatTimestamp(duration);
            setChapters((prev) => [...prev, { time: timestamp, seconds: duration, title: final.trim() }]);
          }

          // Generate subtitle entry
          if (showSubtitles) {
            setSubtitles((prev) => [...prev, {
              id: prev.length + 1,
              start: formatTimestamp(Math.max(0, duration - 3)),
              end: formatTimestamp(duration),
              text: final.trim(),
            }]);
          }

          return updated;
        });
      }

      setInterimText(interim);
    };

    recognition.onerror = (event) => {
      if (event.error !== "no-speech") {
        toast.error(`Erreur de reconnaissance : ${event.error}`);
      }
    };

    recognition.onend = () => {
      if (isListening && !isPaused) {
        // Restart if still listening (continuous mode)
        try { recognition.start(); } catch {}
      }
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
    setIsPaused(false);
    startTimeRef.current = Date.now() - (duration * 1000);

    timerRef.current = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    toast.success("Transcription démarrée");
  }, [language, isListening, isPaused, duration, autoChapters, showSubtitles]);

  // Stop
  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    clearInterval(timerRef.current);
    setIsListening(false);
    setIsPaused(false);
    setInterimText("");
    toast.success("Transcription arrêtée");
  };

  // Pause / Resume
  const togglePause = () => {
    if (isPaused) {
      startTimeRef.current = Date.now() - (duration * 1000);
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
      try { recognitionRef.current?.start(); } catch {}
      setIsPaused(false);
    } else {
      clearInterval(timerRef.current);
      try { recognitionRef.current?.stop(); } catch {}
      setIsPaused(true);
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      clearInterval(timerRef.current);
    };
  }, []);

  // Format timestamp
  const formatTimestamp = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // Format time display
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // Export SRT
  const exportSRT = () => {
    if (subtitles.length === 0) return toast.error("Aucun sous-titre");
    const srt = subtitles.map((s, i) =>
      `${i + 1}\n${s.start.replace(".", ",")} --> ${s.end.replace(".", ",")}\n${s.text}\n`
    ).join("\n");
    downloadFile(srt, "subtitles.srt", "text/plain");
  };

  // Export VTT
  const exportVTT = () => {
    if (subtitles.length === 0) return toast.error("Aucun sous-titre");
    const vtt = "WEBVTT\n\n" + subtitles.map((s) => `${s.start} --> ${s.end}\n${s.text}\n`).join("\n");
    downloadFile(vtt, "subtitles.vtt", "text/vtt");
  };

  // Export transcript
  const exportTranscript = (format) => {
    if (!transcript.trim()) return toast.error("Aucune transcription");
    if (format === "txt") {
      downloadFile(transcript, "transcription.txt", "text/plain");
    } else if (format === "md") {
      let md = `# Transcription\n\n**Date** : ${new Date().toLocaleDateString("fr-FR")}\n**Durée** : ${formatTimestamp(duration)}\n**Mots** : ${wordCount}\n\n---\n\n${transcript}`;
      if (chapters.length > 0) {
        md += `\n\n## Chapitres\n\n${chapters.map((c) => `- **${c.time}** — ${c.title}`).join("\n")}`;
      }
      downloadFile(md, "transcription.md", "text/markdown");
    }
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filename} téléchargé`);
  };

  // Copy
  const copyTranscript = () => {
    navigator.clipboard.writeText(transcript).then(() => toast.success("Copié"));
  };

  // Clear
  const clearAll = () => {
    setTranscript("");
    setInterimText("");
    setSubtitles([]);
    setChapters([]);
    setWordCount(0);
    setDuration(0);
    toast.success("Tout effacé");
  };

  return (
    <RecordingStudioLayout>
      <div className="p-6 md:p-8 space-y-6 max-w-[1200px] mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #8b5cf6, #ec4899)", boxShadow: "0 4px 12px rgba(139,92,246,0.3)" }}>
              <Mic className="h-5 w-5 text-white" />
            </div>
            Voice AI
          </h1>
          <p className="text-sm text-muted-foreground mt-1 ml-[52px]">
            Transcription temps réel, sous-titres automatiques et chapitres IA
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Left: Controls ── */}
          <div className="space-y-4">
            {/* Recording Controls */}
            <Card>
              <CardContent className="pt-5 pb-4 px-5">
                <div className="flex flex-col items-center space-y-4">
                  {/* Mic visualization */}
                  <div className={`relative w-24 h-24 rounded-full flex items-center justify-center ${
                    isListening && !isPaused ? "animate-pulse" : ""
                  }`} style={{
                    background: isListening
                      ? "linear-gradient(135deg, #8b5cf6, #ec4899)"
                      : "linear-gradient(135deg, #374151, #4b5563)",
                    boxShadow: isListening ? "0 0 30px rgba(139,92,246,0.4)" : "none",
                  }}>
                    {isListening && !isPaused ? (
                      <Mic className="h-10 w-10 text-white" />
                    ) : (
                      <MicOff className="h-10 w-10 text-white/60" />
                    )}
                    {isListening && !isPaused && (
                      <>
                        <span className="absolute inset-0 rounded-full border-2 border-purple-400/30 animate-ping" />
                        <span className="absolute inset-[-8px] rounded-full border border-purple-400/10 animate-ping" style={{ animationDelay: "0.3s" }} />
                      </>
                    )}
                  </div>

                  {/* Timer */}
                  <div className="text-center">
                    <p className="text-2xl font-mono font-bold tracking-wider">{formatTimestamp(duration)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{wordCount} mots</p>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-2">
                    {!isListening ? (
                      <Button onClick={startListening} size="lg" className="gap-2 px-6"
                        style={{ background: "linear-gradient(135deg, #8b5cf6, #ec4899)", border: "none" }}>
                        <Mic className="h-5 w-5" /> Démarrer
                      </Button>
                    ) : (
                      <>
                        <Button onClick={togglePause} variant="outline" size="sm" className="gap-1.5">
                          {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                          {isPaused ? "Reprendre" : "Pause"}
                        </Button>
                        <Button onClick={stopListening} variant="destructive" size="sm" className="gap-1.5">
                          <Square className="h-4 w-4" /> Arrêter
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2">
                    {isListening ? (
                      isPaused ? (
                        <Badge variant="outline" className="text-amber-500 border-amber-500/50">En pause</Badge>
                      ) : (
                        <Badge className="bg-emerald-500 text-white animate-pulse">Écoute en cours...</Badge>
                      )
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">Inactif</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Settings */}
            <Card>
              <CardHeader className="pb-3 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-primary" /> Paramètres
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-3">
                <div>
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 block">Langue</label>
                  <Select value={language} onValueChange={setLanguage} disabled={isListening}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value} className="text-xs">{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs flex items-center gap-1.5"><Subtitles className="h-3.5 w-3.5" /> Sous-titres auto</span>
                    <Switch checked={showSubtitles} onCheckedChange={setShowSubtitles} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Chapitres auto</span>
                    <Switch checked={autoChapters} onCheckedChange={setAutoChapters} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Export */}
            <Card>
              <CardHeader className="pb-3 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Download className="h-4 w-4 text-primary" /> Exporter
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-2">
                <Button variant="outline" size="sm" className="w-full text-xs gap-1.5" onClick={() => exportTranscript("txt")} disabled={!transcript}>
                  <FileText className="h-3.5 w-3.5" /> Transcription (.txt)
                </Button>
                <Button variant="outline" size="sm" className="w-full text-xs gap-1.5" onClick={() => exportTranscript("md")} disabled={!transcript}>
                  <BookOpen className="h-3.5 w-3.5" /> Transcription (.md)
                </Button>
                <Button variant="outline" size="sm" className="w-full text-xs gap-1.5" onClick={exportSRT} disabled={subtitles.length === 0}>
                  <Subtitles className="h-3.5 w-3.5" /> Sous-titres (.srt)
                </Button>
                <Button variant="outline" size="sm" className="w-full text-xs gap-1.5" onClick={exportVTT} disabled={subtitles.length === 0}>
                  <Subtitles className="h-3.5 w-3.5" /> Sous-titres (.vtt)
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* ── Right: Transcript + Subtitles ── */}
          <div className="lg:col-span-2 space-y-4">
            {/* Live transcript */}
            <Card className="min-h-[300px]">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Type className="h-4 w-4 text-primary" /> Transcription
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={copyTranscript} disabled={!transcript} title="Copier">
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={clearAll} title="Effacer">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pb-4 px-4">
                {transcript || interimText ? (
                  <div className="text-sm leading-relaxed">
                    <span>{transcript}</span>
                    {interimText && <span className="text-muted-foreground italic">{interimText}</span>}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Mic className="h-12 w-12 text-muted-foreground/15 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {isSupported ? "Cliquez \"Démarrer\" pour commencer la transcription" : "Web Speech API non supportée. Utilisez Chrome."}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chapters */}
            {chapters.length > 0 && (
              <Card>
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-primary" /> Chapitres détectés ({chapters.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-4 px-4">
                  <div className="space-y-1.5">
                    {chapters.map((ch, i) => (
                      <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/50">
                        <Badge variant="outline" className="text-[10px] font-mono">{ch.time}</Badge>
                        <span className="text-xs truncate flex-1">{ch.title}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Subtitles */}
            {subtitles.length > 0 && (
              <Card>
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Subtitles className="h-4 w-4 text-primary" /> Sous-titres ({subtitles.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-4 px-4">
                  <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    {subtitles.map((sub) => (
                      <div key={sub.id} className="flex items-start gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/50 text-xs">
                        <Badge variant="outline" className="text-[9px] font-mono flex-shrink-0 mt-0.5">
                          {sub.start}
                        </Badge>
                        <span className="flex-1">{sub.text}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Browser support note */}
            {!isSupported && (
              <Card className="border-amber-500/30 bg-amber-50 dark:bg-amber-950/20">
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">Navigateur non supporté</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        La Web Speech API nécessite Chrome ou Edge. Firefox et Safari ont un support limité.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </RecordingStudioLayout>
  );
}
